import { memo, useCallback, useEffect } from 'react';
import { TICKET } from 'customer-data-objects/constants/ObjectTypes';
import { LIVE_CHAT_GENERIC_CHANNEL_ID } from 'conversations-message-history/common-message-format/constants/genericChannelIds';
import { isSucceeded } from 'conversations-async-data/async-data/operators/statusComparators';
import { trackEvent } from '../../usage-tracking/clients/usageTracker';
import { getClickedToAssociateCrmObject, getClickedToCreateCrmObject } from '../constants/Events';
import { usePromptReducer } from '../hooks/usePromptReducer';
import ContactSidebarPrompts from './ContactSidebarPrompts';
import { DISASSOCIATE_PROMPT, ASSOCIATE_PROMPT, CREATE_TICKET_PROMPT } from '../constants/PromptTypes';
import ConnectedCrmSidebar from './ConnectedCrmSidebar';
import ContactHighlightSection from './ContactHighlightSection';
import { isKnownContact } from '../../contacts/operators/isKnownContact';
import { useVisitorIdentification } from '../../visitor-identification/hooks/useVisitorIdentification';
import { useThreadOriginalGenericChannelId } from '../../thread-metadata/hooks/useThreadOriginalGenericChannelId';
import AssociateContactToThreadPrompt from './AssociateContactToThreadPrompt';
import OnboardingTour from '../../new-feature-onboarding/components/OnboardingTour';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ContactSidebarComponent = ({
  contact,
  isThreadFiltered,
  isThreadSoftDeleted,
  threadId
}) => {
  const {
    isIdentified,
    visitorIdentificationAsyncData
  } = useVisitorIdentification({
    contact,
    threadId
  });
  const {
    promptStatus,
    openPrompt,
    closePrompt
  } = usePromptReducer();
  const {
    originalGenericChannelId
  } = useThreadOriginalGenericChannelId({
    threadId
  });
  const openCreateTicketModal = useCallback(() => {
    trackEvent(getClickedToCreateCrmObject({
      objectType: TICKET
    }));
    openPrompt(CREATE_TICKET_PROMPT);
  }, [openPrompt]);
  const openAssociateModal = useCallback(() => {
    trackEvent(getClickedToAssociateCrmObject());
    openPrompt(ASSOCIATE_PROMPT);
  }, [openPrompt]);
  const openDisassociateModal = useCallback(() => openPrompt(DISASSOCIATE_PROMPT), [openPrompt]);
  useEffect(() => closePrompt(ASSOCIATE_PROMPT), [threadId, closePrompt]);
  const isKnownVisitor = isKnownContact(contact);
  const isLiveChat = originalGenericChannelId === LIVE_CHAT_GENERIC_CHANNEL_ID;
  const visitorWasNotIdentified = isSucceeded(visitorIdentificationAsyncData) && !isIdentified;
  const shouldDisplayContactWarning = isKnownVisitor && isLiveChat && visitorWasNotIdentified;
  const renderCustomHighlight = useCallback(() => {
    return /*#__PURE__*/_jsx(ContactHighlightSection, {
      contact: contact,
      openAssociateModal: openAssociateModal,
      openDisassociateModal: openDisassociateModal,
      shouldDisplayContactWarning: shouldDisplayContactWarning
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'unknown' is not assignable to type 'boolean'... Remove this comment to see the full error message
      ,
      shouldDisplayIdentifyBadge: isIdentified
    });
  }, [contact, openAssociateModal, openDisassociateModal, shouldDisplayContactWarning, isIdentified]);
  return /*#__PURE__*/_jsx(OnboardingTour, {
    shouldInitialize: false,
    children: /*#__PURE__*/_jsxs("div", {
      className: "height-100 overflow-y-auto selenium-test-marker-contact-sidebar",
      "data-test-id": isKnownVisitor ? 'known-visitor-crm-sidebar' : 'unknown-visitor-crm-sidebar',
      children: [/*#__PURE__*/_jsx(ConnectedCrmSidebar, {
        openCreateTicketModal: openCreateTicketModal,
        contact: contact,
        isThreadFiltered: isThreadFiltered,
        isThreadSoftDeleted: isThreadSoftDeleted,
        renderCustomHighlight: renderCustomHighlight,
        threadId: threadId
      }), /*#__PURE__*/_jsx(ContactSidebarPrompts, {
        closePrompt: closePrompt,
        contact: contact,
        openCreateTicketModal: openCreateTicketModal,
        promptStatus: promptStatus,
        threadId: threadId
      }), /*#__PURE__*/_jsx(AssociateContactToThreadPrompt, {
        closePrompt: closePrompt,
        promptStatus: promptStatus,
        threadId: threadId
      })]
    })
  });
};
ContactSidebarComponent.displayName = 'ContactSidebarComponent';
export const ContactSidebar = /*#__PURE__*/memo(ContactSidebarComponent);
ContactSidebar.displayName = 'ContactSidebar';
export default ContactSidebar;